import * as Sentry from "@sentry/browser";
import { makeAutoObservable, runInAction } from "mobx";
import { type Region } from "config/config";
import { type AnalyticsManager } from "services/analytics/analyticsManager";
import {
  type PartnerOffer,
  type PartnerOfferData,
  type PartnerOfferSource,
  PartnerOfferType,
} from "./partnerOfferTypes";
import { type IPartnerOffersClient } from "./partnerOffersClient";

const KNOWN_TYPES = new Set<string>(Object.values(PartnerOfferType));

export class PartnerOffersStore {
  offers: PartnerOffer[] = [];
  selectedIds: string[] = [];

  constructor(
    private readonly _client: IPartnerOffersClient,
    private readonly _analyticsManager: AnalyticsManager,
  ) {
    makeAutoObservable(this);
  }

  // Built during bootstrap: an empty catalogue means the steps store never adds the step at all.
  static async create(
    client: IPartnerOffersClient,
    analyticsManager: AnalyticsManager,
    country: Region,
    enabled: boolean,
  ): Promise<PartnerOffersStore> {
    const store = new PartnerOffersStore(client, analyticsManager);
    if (enabled) {
      await store._load(country);
    }
    return store;
  }

  get hasOffers(): boolean {
    return this.offers.length > 0;
  }

  get selectedTypes(): string[] {
    return this.offers.filter((offer) => this.isSelected(offer.id)).map((offer) => offer.type);
  }

  isSelected(offerId: string): boolean {
    return this.selectedIds.includes(offerId);
  }

  toggle(offer: PartnerOffer, source: PartnerOfferSource): void {
    const added = !this.isSelected(offer.id);
    this.selectedIds = added ? [...this.selectedIds, offer.id] : this.selectedIds.filter((id) => id !== offer.id);

    this._analyticsManager.event(added ? "partner-offers.add" : "partner-offers.remove", {
      type: offer.type,
      source,
    });
  }

  trackDetailsOpen(offer: PartnerOffer): void {
    this._analyticsManager.event("partner-offers.details-open", { type: offer.type });
  }

  trackDetailsClose(offer: PartnerOffer): void {
    this._analyticsManager.event("partner-offers.details-close", {
      type: offer.type,
      added: this.isSelected(offer.id),
    });
  }

  // A partner offer is a bonus, never a blocker: a failed save costs the selection, not the flow.
  async submit(): Promise<void> {
    this._analyticsManager.event("partner-offers.submit", {
      types: this.selectedTypes,
      count: this.selectedIds.length,
    });

    try {
      const data = await this._client.saveSelection(this.selectedIds);
      const availableIds = new Set(this.offers.map((offer) => offer.id));
      runInAction(() => {
        this.selectedIds = data.selectedOfferIds.filter((id) => availableIds.has(id));
      });
    } catch (error) {
      this._analyticsManager.event("partner-offers.submit-error", { error: String(error) });
      Sentry.captureException(error);
    }
  }

  // A partner offer is a bonus, never a blocker: a failed catalogue costs the step, not the flow.
  private async _load(country: Region): Promise<void> {
    try {
      const data = await this._client.getOffers();
      const offers = data.offers
        .filter((offer) => offer.country === country && this._isKnownType(offer))
        .map((offer) => this._toOffer(offer, country));
      runInAction(() => {
        this.offers = offers;
        const availableIds = new Set(offers.map((offer) => offer.id));
        this.selectedIds = data.selectedOfferIds.filter((id) => availableIds.has(id));
      });
    } catch (error) {
      this._analyticsManager.event("partner-offers.load-error", { error: String(error) });
      Sentry.captureException(error);
    }
  }

  // The backend can add a partner before the frontend has copy for it: such an offer is dropped, the
  // rest of the catalogue keeps working.
  private _isKnownType(offer: PartnerOfferData): boolean {
    if (KNOWN_TYPES.has(offer.type)) {
      return true;
    }

    Sentry.captureMessage(`Unknown partner offer type: ${offer.type}`);
    return false;
  }

  private _toOffer(offer: PartnerOfferData, country: Region): PartnerOffer {
    return { id: offer.id, type: offer.type as PartnerOfferType, country };
  }
}
